// Global Instructions Rule Applied!
//
// Funds tab — this LP's relationship with our funds, both halves of it: the
// money they committed (Fund Commitments) over every fund we ever put in front
// of them and where it got to (Fundraising History).
//
// It was the Commitments tab until 2026-09-06, gated on
// prog__commitments_count > 0 rather than on the LP role — so an LP with three
// live fundraising processes and no commitment yet had no tab at all. That is
// precisely the LP whose history says the most (declined, stopped out, still
// open), which is why the history could not simply be moved here without the
// gate widening first. With the gate on the role, "Commitments" was the wrong
// name for a tab that shows when there are none.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { Button as AntButton, Table } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import Button from '../../../../../core/components/Button.jsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { exportJsonToExcel } from '../../../../../core/services/utilitiesService.ts'
import { fetchAccountCommitments } from '../../../services/commitmentsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { groupCommitmentsByFund, type FundGroupRow } from '../../../utils/groupCommitmentsByFund.ts'
import { LpFundraisingHistorySection } from '../../../../internal_agents/components/LpFundraisingHistorySection.tsx'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../../core/hooks/useRefreshOnActivate.ts'
import { TABLE_COMMITMENTS, TABLE_INVESTORS } from '../../../../../state_manager/globals.ts'
import {
  commitmentsColumnsConfig,
  prepareDataForExport
} from '../../../../../ui_configs/fund_management/investing/companies/views/commitments_table_columns.ts'

interface FundsTabProps {
  recordId?: string | null
  accountName?: string | null
  /** Hands ONE refresher up that reloads both sections, so the form's Refresh
   *  drives the whole tab (RelationshipTab's contract). */
  onRefreshCommitmentsReady?: (refreshFn: () => Promise<void>) => void
}

type ColumnConfig = (typeof commitmentsColumnsConfig)[number]
const NUMERIC_FIELD_TYPES: ColumnConfig['fieldType'][] = ['number', 'currency', 'valuation']
// Index of the Commitment-amount column within commitmentsColumnsConfig — the
// summary puts the per-currency total in this column so it lines up with the body.
const AMOUNT_COL_INDEX = commitmentsColumnsConfig.findIndex((c) => c.valueKey === 'fund_commitments__amount_fmt')

function sanitizeForFilename(s: string): string {
  const sanitized = s.replace(/[/\\:*?"<>|]/g, '-').trim()
  return sanitized || 'Unknown'
}

const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

type OpenInvestor = (investorId: string, investorName: string) => void

// One render branching on the row `kind`: a fund parent row only carries a name +
// the per-fund total; commitment / single rows carry the full per-commitment detail.
// On those the investor name is the record link to the Investor form; the fund
// parent row's entity label stays text.
function renderCell(
  col: ColumnConfig,
  isNumeric: boolean,
  value: unknown,
  record: FundGroupRow,
  openInvestor: OpenInvestor
): React.ReactNode {
  const kind = record.kind

  if (col.valueKey === 'funds__name') {
    return kind === 'commitment' ? '' : disp(record.funds__name)
  }

  if (col.valueKey === 'investors__name') {
    if (kind === 'fund') return <span className='text-gray-500'>{record.entity_label}</span>
    const name = disp(record.investors__name)
    const investorId = typeof record.investors__id === 'string' ? record.investors__id : ''
    const cell = investorId ? (
      <button
        type='button'
        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
        onClick={(e) => {
          // the row itself expands / collapses on click
          e.stopPropagation()
          openInvestor(investorId, name)
        }}
      >
        {name}
      </button>
    ) : (
      name
    )
    return kind === 'commitment' ? <span style={{ paddingLeft: 12 }}>{cell}</span> : cell
  }

  if (col.valueKey === 'fund_commitments__amount_fmt') {
    return <div className='text-right'>{disp(record.fund_commitments__amount_fmt)}</div>
  }

  // Every other column is blank on the aggregate (fund parent) rows.
  if (kind === 'fund') return ''
  return isNumeric ? <div className='text-right'>{disp(value)}</div> : disp(value)
}

function buildColumns(openInvestor: OpenInvestor): ColumnsType<FundGroupRow> {
  return commitmentsColumnsConfig.map((col) => {
    const isNumeric = Boolean(col.fieldType && NUMERIC_FIELD_TYPES.includes(col.fieldType))
    return {
      title: col.columnTitle,
      dataIndex: col.valueKey,
      key: col.valueKey,
      width: col.width,
      render: (value: unknown, record: FundGroupRow) => renderCell(col, isNumeric, value, record, openInvestor)
    }
  })
}

export function FundsTab({ recordId, accountName, onRefreshCommitmentsReady }: FundsTabProps) {
  const { openFormInTab } = useTabManagerContext()
  const [loading, setLoading] = useState(false)
  const historyRef = useRef<(() => Promise<void>) | null>(null)
  const [exporting, setExporting] = useState(false)
  // Raw service rows (commitment rows + the per-currency Total rows). Display rows,
  // the fixed total summary and the export rows are all derived from these.
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([])

  const fetchData = useCallback(async () => {
    if (!recordId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchAccountCommitments(recordId, { pageSize: 1000 }, token)
      if (result.error) {
        showErrorToast(result.error)
        setRawRows([])
        return
      }
      setRawRows(result.data)
    } finally {
      setLoading(false)
    }
  }, [recordId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // One refresher for both sections — the form's Refresh button reloads the
  // whole tab (RelationshipTab's refreshAll contract).
  const refreshAll = useCallback(async () => {
    await Promise.all([fetchData(), historyRef.current?.()])
  }, [fetchData])

  useEffect(() => {
    if (onRefreshCommitmentsReady) onRefreshCommitmentsReady(refreshAll)
  }, [onRefreshCommitmentsReady, refreshAll])

  // a commitment added on the New commitment form, or an investor renamed on
  // the Investor form (each in its own tab), shows here when the user comes
  // back
  const activateRef = useRefreshOnActivate(fetchData, { maxAgeMs: 0 })

  const columns = useMemo(
    () => buildColumns((investorId, investorName) => openFormInTab(TABLE_INVESTORS, investorId, investorName)),
    [openFormInTab]
  )

  // Fund-grouped, expandable rows. The per-currency grand total is rendered as a
  // fixed table summary so it lines up with the columns and stays visible.
  const dataSource = useMemo<FundGroupRow[]>(() => groupCommitmentsByFund(rawRows), [rawRows])
  const totalRows = useMemo(() => rawRows.filter((r) => r._total_label), [rawRows])
  const commitmentCount = useMemo(() => rawRows.filter((r) => !r._total_label).length, [rawRows])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // Export the flat commitment rows (exclude the synthesised Total rows).
      const exportRows = rawRows.filter((r) => !r._total_label)
      const { data, columns: exportColumns, dateColumns } = prepareDataForExport(exportRows, commitmentsColumnsConfig)
      const date = dayjs().format('YYYY-MM-DD')
      const filename = `${sanitizeForFilename(accountName || 'Unknown')} - Commitments - ${date}.xlsx`
      const result = await exportJsonToExcel({ data, columns: exportColumns, filename, dateColumns }, token)
      if (result.success) toast.success(`${filename} exported successfully`)
      else showErrorToast(result.error || 'Export failed')
    } finally {
      setExporting(false)
    }
  }, [rawRows, accountName])

  const rowProps = (record: FundGroupRow) => {
    if (record.kind === 'fund' || record.kind === 'fund-single') return { className: 'bg-gray-50 font-medium' }
    return {}
  }

  // Column-aligned fixed total summary — one row per currency. "Total" sits under
  // the Fund column, the amount under the Commitment column.
  const renderSummary = () => (
    <Table.Summary fixed>
      {totalRows.map((t, i) => (
        <Table.Summary.Row
          key={String(t.currency_code ?? i)}
          style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}
        >
          {commitmentsColumnsConfig.map((col, idx) => {
            if (idx === 0) {
              return <Table.Summary.Cell key={col.valueKey} index={0}>Total</Table.Summary.Cell>
            }
            if (idx === AMOUNT_COL_INDEX) {
              return (
                <Table.Summary.Cell key={col.valueKey} index={idx} align='right'>
                  {disp(t.fund_commitments__amount_fmt)}
                </Table.Summary.Cell>
              )
            }
            return <Table.Summary.Cell key={col.valueKey} index={idx} />
          })}
        </Table.Summary.Row>
      ))}
    </Table.Summary>
  )

  return (
    // The tab's own scroller, with the two sections stacked inside it — the
    // RelationshipTab layout. It replaces a ResizeObserver that sized the
    // table's scroll.y from the tab height, which assumed the commitments
    // section owned the whole tab. The per-currency Total summary now sits
    // under the last row instead of being pinned.
    <div
      ref={activateRef}
      data-debug='company-funds-tab-container'
      data-component='FundsTab'
      className='h-full overflow-auto'
    >
      <div className='flex flex-col gap-2'>
      <FormSection
        title='Fund Commitments'
        titleColor='blue'
        headerActions={
          <>
            <Button
              variant='default'
              size='small'
              icon={<FontAwesomeIcon icon={faDownload} />}
              onClick={handleExport}
              loading={exporting}
              disabled={commitmentCount === 0}
            >
              Export
            </Button>
            {/* a commitment for this account, to a fund or a consolidated
                fund; a new investor entity can be created in the same step */}
            <AntButton
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={!recordId}
              onClick={() => openFormInTab(TABLE_COMMITMENTS, null, 'New Commitment', { account: recordId })}
            >
              Commitment
            </AntButton>
          </>
        }
      >
        <div>
          <Table<FundGroupRow>
            rowKey='key'
            dataSource={dataSource}
            columns={columns}
            loading={loading}
            pagination={false}
            size='small'
            scroll={{ x: 'max-content' }}
            onRow={(record) => rowProps(record)}
            summary={totalRows.length > 0 ? renderSummary : undefined}
            expandable={{
              rowExpandable: (record) => record.kind === 'fund',
              expandRowByClick: true,
              expandIcon: ({ expanded, onExpand, record }) =>
                record.kind === 'fund' ? (
                  // antd hands onExpand an HTML mouse event; the icon itself is an SVG, so the click lives on a span
                  <span
                    className='cursor-pointer inline-flex'
                    onClick={(e) => {
                      e.stopPropagation()
                      onExpand(record, e)
                    }}
                  >
                    <FontAwesomeIcon
                      icon={expanded ? faChevronDown : faChevronRight}
                      className='text-gray-500'
                      style={{ marginRight: 8 }}
                    />
                  </span>
                ) : (
                  <span style={{ display: 'inline-block', width: 14, marginRight: 8 }} />
                )
            }}
            locale={{
              emptyText: <div className='text-center text-gray-500 text-sm py-2'>No fund commitments</div>
            }}
          />
        </div>
      </FormSection>

      {/* Every fund we put in front of this LP and where it got to — including
          the ones that never became money. No hideWhenEmpty: the tab is
          LP-scoped, and an empty history is an honest answer (the reasoning
          that kept it visible on the Relationship tab, where it lived until
          2026-09-06). */}
      <LpFundraisingHistorySection
        accountId={recordId}
        accountName={accountName}
        onRefreshReady={(fn) => {
          historyRef.current = fn
        }}
      />
      </div>
    </div>
  )
}
